import { StartClassificationAndExtractionResponse } from "@dune/types";
import { useQueryClient } from "@tanstack/react-query";
import { DownloadCloud01, RefreshCcw01 } from "@untitled-ui/icons-react";
import JSZip from "jszip";
import { useState } from "react";
import { useStartClassificationAndExtraction } from "../../api/mutations/ai-jobs.mutations";
import {
	getSignedPutUrl,
	registerS3File,
	uploadFileToS3,
	useFolderHasAiJobRunning,
	useGetFilesWithMetadata,
} from "../../api/queries/files.queries";
import { filesQueryKeys } from "../../api/query-keys/files.query-keys";
import { getMimeTypeFromFileContent } from "../../utils/mime-type.utils";
import FileWithAiJobCard from "./FileWithAiJobCard";

type FileDropUploadAndProcessWithAiInputProps = {
	bucket?: "private" | "public";
	folderId?: string;
	existingFileIds?: string[];
	onUploadedFiles?: (fileIds: string[]) => void;
	onDeleteOneFile?: (fileId: string) => void;
	disabled?: boolean;
};

const MAX_EXTRACTION_DEPTH = 5;
const MAX_FILES_PER_ARCHIVE = 1000;
const MAX_TOTAL_SIZE = 500 * 1024 * 1024;
const MAX_FILE_SIZE = 100 * 1024 * 1024;

interface ExtractionContext {
	depth: number;
	totalFilesExtracted: number;
	totalSizeExtracted: number;
}

export default function FileDropUploadAndProcessWithAiInput({
	bucket = "private",
	folderId,
	existingFileIds = [],
	onDeleteOneFile,
	onUploadedFiles,
	disabled,
}: FileDropUploadAndProcessWithAiInputProps) {
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [fileIds, setFileIds] = useState<string[]>(existingFileIds);
	const [startAiJobResponseError, setStartAiJobResponseError] = useState<
		StartClassificationAndExtractionResponse["message"]["files_failed"]
	>([]);
	const [isRefreshCooldown, setIsRefreshCooldown] = useState(false);

	const startClassificationMutation = useStartClassificationAndExtraction({
		onSuccess: (data) => {
			if (data.message.files_failed.length > 0) {
				setStartAiJobResponseError(data.message.files_failed);
			}
			queryClient.invalidateQueries({
				queryKey: filesQueryKeys.folderHasAiJobRunning(folderId ?? ""),
			});
		},
	});

	const { data: folderHasAiJobRunning } = useFolderHasAiJobRunning(
		folderId ?? "",
	);

	const {
		data: files,
		isLoading: isLoadingFiles,
		isFetching: isFetchingFiles,
	} = useGetFilesWithMetadata(fileIds);

	const queryClient = useQueryClient();

	const uploadSingleFile = async (file: File): Promise<string> => {
		const signed = await getSignedPutUrl({
			bucket: bucket,
			contentType: file.type,
		});

		await uploadFileToS3(signed.signedUrl, file);

		const registeredFile = await registerS3File({
			bucket: bucket,
			key: signed.key,
			name: file.name,
			mimeType: file.type,
			size: file.size,
			folderId,
		});

		return registeredFile.id;
	};

	const processZipFile = async (
		zipFile: File | Blob,
		context: ExtractionContext = {
			depth: 0,
			totalFilesExtracted: 0,
			totalSizeExtracted: 0,
		},
		parentPath = "",
	): Promise<string[]> => {
		if (context.depth >= MAX_EXTRACTION_DEPTH) {
			console.warn(
				`Profondeur maximale d'extraction atteinte (${MAX_EXTRACTION_DEPTH})`,
			);
			return [];
		}

		if (context.totalFilesExtracted >= MAX_FILES_PER_ARCHIVE) {
			console.warn(
				`Nombre maximum de fichiers atteint (${MAX_FILES_PER_ARCHIVE})`,
			);
			return [];
		}

		if (context.totalSizeExtracted >= MAX_TOTAL_SIZE) {
			console.warn(
				`Taille maximale d'extraction atteinte (${MAX_TOTAL_SIZE} bytes)`,
			);
			return [];
		}

		const zip = await JSZip.loadAsync(zipFile);
		const entries = Object.keys(zip.files);
		const uploadedIds: string[] = [];

		for (const filename of entries) {
			if (context.totalFilesExtracted >= MAX_FILES_PER_ARCHIVE) {
				console.warn(
					`Arrêt de l'extraction: nombre maximum de fichiers atteint`,
				);
				break;
			}

			if (context.totalSizeExtracted >= MAX_TOTAL_SIZE) {
				console.warn(`Arrêt de l'extraction: taille maximale atteinte`);
				break;
			}

			if (filename.startsWith("__MACOSX/") || filename.startsWith("."))
				continue;

			const entry = zip.files[filename];
			if (!entry.dir) {
				const entryWithSize = entry as JSZip.JSZipObject & {
					_data?: { uncompressedSize?: number };
					uncompressedSize?: number;
				};
				const uncompressedSize =
					entryWithSize._data?.uncompressedSize ||
					entryWithSize.uncompressedSize ||
					0;
				if (uncompressedSize > MAX_FILE_SIZE) {
					console.warn(
						`Fichier trop volumineux ignoré: ${filename} (${uncompressedSize} bytes)`,
					);
					continue;
				}

				if (context.totalSizeExtracted + uncompressedSize > MAX_TOTAL_SIZE) {
					console.warn(
						`Fichier ignoré pour éviter de dépasser la taille limite: ${filename}`,
					);
					continue;
				}

				const blob = await entry.async("blob");
				const detectedMimeType = await getMimeTypeFromFileContent(
					blob,
					filename,
				);

				const fullPath = parentPath ? `${parentPath}/${filename}` : filename;

				if (
					detectedMimeType === "application/zip" ||
					filename.toLowerCase().endsWith(".zip")
				) {
					const nestedContext: ExtractionContext = {
						depth: context.depth + 1,
						totalFilesExtracted: context.totalFilesExtracted,
						totalSizeExtracted: context.totalSizeExtracted + blob.size,
					};

					try {
						const nestedIds = await processZipFile(
							blob,
							nestedContext,
							fullPath,
						);
						uploadedIds.push(...nestedIds);

						context.totalFilesExtracted += nestedIds.length;
						context.totalSizeExtracted = nestedContext.totalSizeExtracted;
					} catch (error) {
						console.error(
							`Erreur lors du traitement du ZIP imbriqué ${fullPath}:`,
							error,
						);
					}
				} else {
					const extractedFile = new File([blob], fullPath, {
						type: detectedMimeType,
					});

					try {
						const fileId = await uploadSingleFile(extractedFile);
						uploadedIds.push(fileId);

						context.totalFilesExtracted++;
						context.totalSizeExtracted += blob.size;
					} catch (error) {
						console.error(
							`Erreur lors de l'upload du fichier ${fullPath}:`,
							error,
						);
					}
				}
			}
		}

		return uploadedIds;
	};

	const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
		setError(null);
		const files = e.target.files;
		if (!files || files.length === 0) return;

		setUploading(true);

		try {
			const allUploadedIds: string[] = [];

			for (const file of Array.from(files)) {
				if (file.type === "application/zip" || file.name.endsWith(".zip")) {
					const zipFileIds = await processZipFile(file);
					allUploadedIds.push(...zipFileIds);
				} else {
					const fileId = await uploadSingleFile(file);
					allUploadedIds.push(fileId);
				}
			}

			setFileIds((prev) => [...prev, ...allUploadedIds]);
			onUploadedFiles?.(allUploadedIds);
			if (allUploadedIds.length > 0) {
				startClassificationMutation.mutate(allUploadedIds);
			}
		} catch (_e) {
			setError("Erreur lors du traitement des fichiers");
		} finally {
			setUploading(false);

			e.target.value = "";
		}
	};

	const handleRemove = (fileIdToRemove: string) => {
		setFileIds((previousFileIds) =>
			previousFileIds.filter((fileId) => fileId !== fileIdToRemove),
		);
		onDeleteOneFile?.(fileIdToRemove);
	};

	const handleRefresh = () => {
		if (isRefreshCooldown) return;

		setIsRefreshCooldown(true);
		queryClient.invalidateQueries({ queryKey: filesQueryKeys.details() });
		queryClient.invalidateQueries({
			queryKey: filesQueryKeys.folderHasAiJobRunning(folderId ?? ""),
		});

		setTimeout(() => {
			setIsRefreshCooldown(false);
		}, 3000);
	};

	const filesMap = new Map(files?.map((file) => [file.id, file]) ?? []);

	return (
		<div className="w-full">
			<label
				className={`block w-full ${
					uploading || disabled
						? "cursor-not-allowed opacity-60"
						: "cursor-pointer"
				}`}
			>
				<div
					className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-8 transition-colors ${
						uploading || disabled
							? "bg-gray-100 border-gray-200"
							: "bg-white hover:bg-accent/50"
					}`}
				>
					<DownloadCloud01
						className={`w-8 h-8 mb-2 ${
							uploading || disabled ? "text-gray-300" : "text-blue-400"
						}`}
					/>
					<span
						className={`font-semibold ${
							uploading || disabled ? "text-gray-400" : "text-text-blue"
						}`}
					>
						Cliquez pour ajouter
					</span>{" "}
					ou glisser-déposer des fichiers (tous formats supportés)
					<input
						type="file"
						multiple
						className="hidden"
						onChange={handleFiles}
						disabled={uploading || disabled}
					/>
				</div>
			</label>
			{error && <div className="text-red-500 mt-2">{error}</div>}
			{uploading && (
				<div className="mt-2 text-blue-500">
					Traitement et upload des fichiers en cours...
				</div>
			)}
			{folderHasAiJobRunning && (
				<div className="bg-background-blue text-white h-8 flex items-center justify-center relative rounded mt-4">
					Vos documents ont bien été pris en compte, vous recevrez un email une
					fois leur traitement terminé.
				</div>
			)}
			{fileIds.length > 0 && (
				<div className="mt-8">
					<div className="flex items-center justify-between mb-4">
						<h3 className="text-lg font-medium text-gray-900">
							Fichiers uploadés ({fileIds.length})
						</h3>
						<button
							type="button"
							onClick={handleRefresh}
							disabled={isRefreshCooldown}
							className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${
								isRefreshCooldown
									? "text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed"
									: "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
							}`}
						>
							<RefreshCcw01
								className={`w-4 h-4 ${isRefreshCooldown ? "animate-spin" : ""}`}
							/>
							{isRefreshCooldown ? "Actualisation..." : "Actualiser le statut"}
						</button>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
						{fileIds.map((fileId) => (
							<FileWithAiJobCard
								key={fileId}
								id={fileId}
								file={filesMap.get(fileId)}
								isLoading={isLoadingFiles}
								isFetching={isFetchingFiles}
								onRemove={handleRemove}
								startAiJobResponseError={
									startAiJobResponseError.find(
										(error) => error.file_id === fileId,
									)?.error_message
								}
								readonly={disabled}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
